import * as SelectPrimitive from "@radix-ui/react-select";
import useChangeSetting from "components/settings/hooks/useChangeSetting";
import { FatSelectItem, Select, SelectContent, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import useSelfie from "lib/hooks/useSelfie";
import { useRouter } from "next/router";
import { invariant } from "outvariant";
import { FormattedDisplayName } from "react-intl";

export const AVAILABLE_LANGUAGES = ["en-US", "pt-BR"];

interface LanguageSelectorProps {
  disabled?: boolean;
}
export default function LanguageSelector({ disabled = false }: LanguageSelectorProps) {
  const viewer = useSelfie();
  const { locale = "pt-BR", pathname, asPath, query, push } = useRouter();
  const { set: setLanguageSetting } = useChangeSetting("language", "pt-BR");
  invariant(AVAILABLE_LANGUAGES.includes(locale), `Couldn't match on language for "%s"`, locale);

  const isoCountryCode = locale.split("-")[1] as "US" | "BR";
  invariant(["US", "BR"].includes(isoCountryCode), `Couldn't match on country code for "%s"`, locale);

  return (
    <Select
      disabled={disabled}
      value={locale}
      onValueChange={(nextLocale) => {
        setLanguageSetting(nextLocale as "en-US" | "pt-BR");
        push({ pathname, query }, asPath, {
          locale: nextLocale,
        });
      }}
    >
      <SelectPrimitive.Trigger
        className={classNames(
          "flex h-10 w-10 items-center justify-center rounded-xl bg-neutral-100 text-gray-700 dark:bg-slate-700",
          "border border-transparent",
          "outline-none",
          "focus-visible:bg-indigo-50 focus-visible:ring-2 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
          "hover:bg-gray-50 hover:ring-2 hover:ring-gray-100 hover:ring-offset-1 hover:ring-offset-gray-300",
          "dark:hover:ring-slate-500 dark:hover:ring-offset-slate-500",
          {
            "bg-neutral-700 text-neutral-200 hover:bg-neutral-500": viewer && viewer.isBlack,
          },
        )}
      >
        <SelectValue asChild={true}>
          <span className="inline-flex">
            <CountryFlag isoCode={isoCountryCode} width={24} height={24} className="rounded-xl" />
          </span>
        </SelectValue>
      </SelectPrimitive.Trigger>

      <SelectContent
        className={classNames("rounded-xl border border-slate-200 text-sm dark:border-slate-950")}
        side="top"
      >
        {AVAILABLE_LANGUAGES.map((locale) => {
          const isoCountryCode = locale.split("-")[1] as "US" | "BR";
          invariant(["US", "BR"].includes(isoCountryCode), `Couldn't match on country code for "%s"`, locale);

          return (
            <FatSelectItem key={locale} value={locale} className="cursor-pointer space-x-4 rounded-xl">
              <div className="flex w-44 items-center space-x-3">
                <CountryFlag isoCode={isoCountryCode} width={18} height={18} className="rounded-xl" />

                <div>
                  <h5 className="text-xs font-bold">
                    <FormattedDisplayName type="language" value={locale} />
                  </h5>
                  <h6 className="text-xs text-slate-500">
                    <FormattedDisplayName type="region" value={isoCountryCode} />
                  </h6>
                </div>
              </div>
            </FatSelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}

interface CountryFlagProps {
  isoCode: "US" | "BR";
}
function CountryFlag({ isoCode, ...props }: CountryFlagProps & React.SVGProps<any>) {
  // Grab that stuff from: https://s.fndm.to/svg/flags/squared/US.svg
  if (isoCode === "US") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" {...props}>
        <g fill="none" fillRule="evenodd">
          <path fill="#F8F9FD" d="M0 0h18v18H0z" />
          <path fill="#EF5350" d="M11 4h7V0h-7v4zM0 18h18v-4H0v4zm11-7h7V7h-7v4z" />
          <path fill="#1E88E5" d="M0 0h11v11H0z" />
          <path
            fill="#F8F9FD"
            d="M2.42 1.22a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm3-6a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm3-6a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7zm0 3a.09.09 0 0 1 .16 0l.27.71c.01.04.05.06.08.06l.76.04c.08 0 .11.1.05.16l-.6.47a.09.09 0 0 0-.02.1l.2.72a.09.09 0 0 1-.14.1l-.63-.41a.09.09 0 0 0-.1 0l-.63.41a.09.09 0 0 1-.14-.1l.2-.73a.09.09 0 0 0-.03-.1l-.59-.46a.09.09 0 0 1 .05-.16l.76-.04c.03 0 .07-.02.08-.06l.27-.7z"
          />
        </g>
      </svg>
    );
  }

  if (isoCode === "BR") {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" {...props}>
        <g fill="none" fillRule="evenodd">
          <path fill="#FDD835" d="M0 0h18v18H0V0z" />
          <path fill="#4CAF50" d="M18 0H0v7.4l9-5.62 9 5.62V0zm0 10.6-8.92 5.62-.04-.03L0 10.6V18h18v-7.4z" />
          <path fill="#1565C0" d="M13 8.99A4 4 0 1 1 5 9a4 4 0 0 1 8 0z" />
          <path
            fill="#fff"
            d="M8.6 8.83A7.5 7.5 0 0 0 5 8.61c.12-.96.28-1.07.28-1.07 1-.13 2.32-.06 3.61.33a7.36 7.36 0 0 1 3.83 2.61s-.23.64-.56.96a6.26 6.26 0 0 0-3.56-2.6z"
          />
        </g>
      </svg>
    );
  }

  throw new Error(`Invalid country code provided: ${isoCode}`);
}
